import React, { useCallback, useEffect, useState } from 'react'

import { Box, Container, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ErrorBoundary } from '@scoir/error-reporter'
import Table from 'material/MuiTable'
import useTableContext from 'material/MuiTable/use-table-context'

import api from 'common/api'
import { formatter } from 'common/date'
import { isSlateActive } from 'common/integrations-helpers'
import useTitle from 'common/use-title'
import { useDocumentDelegateName } from 'store/college/hooks'
import { useScid } from 'store/user/hooks'

import DownloadAction from './DownloadActionCell'
import DownloadManifestAction from './DownloadManifestActionCell'
import { Principle } from './Principle'
import SentDateCell from './SentDateCell'

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
  },
  headerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  headerSubWrapper: {
    marginBottom: `${theme.spacing(1)}px`,
    [theme.breakpoints.up('md')]: {
      marginBottom: '0',
    },
  },
  delegateBox: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
}))

export const DailyBatches = () => {
  useTitle('Scoir - Document Batches')
  const classes = useStyles()
  const [results, setResults] = useState([])
  const [hasError, setHasError] = useState(false)
  const [total, setTotal] = useState(0)
  const [refresh, setRefresh] = useState(false)
  const [integrations, setIntegrations] = useState({
    SlateConfigFileRequired: false,
  })
  const { page, pageSize, sort, sortDir, updateContext } = useTableContext({
    defaultSortKey: 'SentDate',
    defaultSortDir: 'desc',
    pageSize: 25,
  })

  const scid = useScid()
  const documentDelegateName = useDocumentDelegateName()

  const fetchResults = useCallback(
    (ctx) => {
      const queryParams = {
        sort: ctx.sortDir === 'desc' ? `-${ctx.sort}` : ctx.sort,
        from: `${ctx.page * ctx.pageSize}`,
        pageSize: `${ctx.pageSize}`,
      }

      if (refresh) {
        queryParams.from = '0'
      }
      const params = new URLSearchParams(queryParams)
      setHasError(false)
      return api
        .get(`/api/colleges/${scid}/batches?${params.toString()}`)
        .then((results) => {
          setTotal(results.Total)
          setResults(results.Batches ? results.Batches : [])
        })
        .catch(() => {
          setHasError(true)
        })
    },
    [scid],
  )
  useEffect(() => {
    api
      .get(`/api/colleges/${scid}/integrations`)
      .then((integrations) => {
        setIntegrations({
          SlateConfigFileRequired: false,
          ...integrations,
        })
      })
      .catch(() => {
        setHasError(true)
      })
  }, [scid])
  useEffect(() => {
    if (refresh) {
      setRefresh(false)
      updateContext({ page: 0 })
    } else {
      fetchResults({
        page,
        pageSize,
        sort,
        sortDir,
      })
    }
  }, [fetchResults, sort, sortDir, pageSize, page, refresh])

  return (
    <Box mb={5}>
      <ErrorBoundary>
        <Container maxWidth="lg">
          <Box>
            {documentDelegateName && (
              <Box
                my={2}
                className={classes.delegateBox}
              >
                <Principle />
                <Typography>
                  <strong>{documentDelegateName}</strong> is currently receiving your application materials &amp;
                  document batches.
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            mt={4}
            className={classes.headerWrapper}
          >
            <Box
              mr={1}
              minWidth={250}
              className={classes.headerSubWrapper}
            >
              <Typography
                variant="h6"
                component="h1"
                className={classes.pageHeader}
              >
                Historical Batches
              </Typography>
            </Box>
          </Box>
          <Paper>
            <Table
              records={results}
              keyBy="Id"
              columns={[
                {
                  label: 'Batch Date',
                  render: (record) => <SentDateCell record={record} />,
                  sortKey: 'SentDate',
                  id: 'SentDate',
                },
                {
                  label: 'Staff',
                  render: (record) => `${record.StaffFirstName} ${record.StaffLastName}`,
                  id: 'StaffLastName',
                },
                {
                  label: 'Downloaded Date',
                  render: (record) => formatter.format(record.DeliveredDate) || <>&mdash;</>,
                  id: 'DeliveredDate',
                },
                {
                  label: '# Students',
                  render: (record) => record.NumStudents,
                  id: 'NumStudents',
                },
                {
                  label: '# Items',
                  render: (record) => record.NumDocuments,
                  id: 'NumDocuments',
                },
                {
                  label: 'Action',
                  id: 'Action',
                  render: (record) => (
                    <>
                      <DownloadAction
                        record={record}
                        onSuccess={() => {
                          fetchResults({
                            page,
                            pageSize,
                            sort,
                            sortDir,
                          })
                        }}
                      />
                      {(integrations.SlateConfigFileRequired || isSlateActive(integrations)) && (
                        <Box
                          ml={1}
                          display="inline-block"
                        >
                          <DownloadManifestAction record={record} />
                        </Box>
                      )}
                    </>
                  ),
                },
              ]}
              total={total}
              order={sortDir}
              orderBy={sort}
              page={page}
              rowsPerPage={pageSize}
              onSortChange={(sort, sortDir) =>
                updateContext({
                  page: 0,
                  sort,
                  sortDir,
                  pageSize,
                })
              }
              onPageSizeChange={(pageSize) =>
                updateContext({
                  page: 0,
                  pageSize,
                })
              }
              onPageChange={(page) => updateContext({ page })}
            />
            {hasError && (
              <Box
                mt={1}
                pb={2}
              >
                <Typography
                  role="alert"
                  color="error"
                  align="center"
                >
                  An error occurred while loading the applicants
                </Typography>
              </Box>
            )}
          </Paper>
        </Container>
      </ErrorBoundary>
    </Box>
  )
}

export default DailyBatches
